import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'];
const METHOD_OPTIONS = ['', 'CASH', 'BANK_TRANSFER', 'MOBILE_MONEY', 'CARD', 'OTHER'];
const METHOD_LABELS: Record<string, string> = {
  CASH: 'Cash', BANK_TRANSFER: 'Bank Transfer', MOBILE_MONEY: 'Mobile Money', CARD: 'Card', OTHER: 'Other',
};
const LIMIT = 20;

export const RecoveryPayments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [methodFilter, setMethodFilter] = useState('');
  const [trackingSearch, setTrackingSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  // inline complete form
  const [completeId, setCompleteId] = useState('');
  const [txRef, setTxRef] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (methodFilter) filters.paymentMethod = methodFilter;
      if (trackingSearch) filters.trackingNumber = trackingSearch;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.listPayments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setPayments(d?.payments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, statusFilter, methodFilter, trackingSearch, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const fail = async (id: string) => {
    const reason = window.prompt('Reason for failing this payment:');
    if (reason === null) return;
    setActionId(id); setError('');
    try { await apiClient.failPayment(id, reason); load(); }
    catch (e: any) { setError(e.response?.data?.error || 'Failed'); }
    finally { setActionId(''); }
  };

  const complete = async (id: string) => {
    setActionId(id); setError('');
    try {
      await apiClient.completePayment(id, { transactionReference: txRef || undefined });
      setCompleteId(''); setTxRef('');
      load();
    }
    catch (e: any) { setError(e.response?.data?.error || 'Failed'); }
    finally { setActionId(''); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Payments</h1>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      <div className="card">
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              placeholder="Search tracking number..."
              value={trackingSearch}
              onChange={e => { setTrackingSearch(e.target.value); setPage(0); }}
              style={{ paddingLeft: 32, width: '100%' }}
            />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
          <select value={methodFilter} onChange={e => { setMethodFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {METHOD_OPTIONS.map(m => <option key={m} value={m}>{m ? METHOD_LABELS[m] : 'All Methods'}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" style={{ width: 145 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" style={{ width: 145 }} />
        </div>

        {loading ? <div className="loading-spinner">Loading payments...</div> : payments.length === 0 ? (
          <div className="empty-state"><h3>No payments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Shipment</th><th>Amount</th><th>Method</th><th>Status</th><th>Due Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {payments.map((p: any) => (
                    <React.Fragment key={p.id}>
                      <tr>
                        <td><code style={{ fontSize: 11 }}>{p.Shipment?.trackingNumber || p.shipment?.trackingNumber || p.shipmentId?.slice(0, 8)}</code></td>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{Number(p.amount).toLocaleString()} {p.currency}</td>
                        <td>{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</td>
                        <td><span className={`badge badge-${p.status?.toLowerCase()}`}>{p.status}</span></td>
                        <td style={{ fontSize: 12 }}>{p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {p.status === 'PENDING' && (
                              <>
                                <button
                                  className="btn btn-success"
                                  style={{ padding: '3px 8px', fontSize: 12 }}
                                  onClick={() => { setCompleteId(completeId === p.id ? '' : p.id); setTxRef(''); setError(''); }}
                                >
                                  Complete
                                </button>
                                <button
                                  className="btn btn-danger"
                                  style={{ padding: '3px 8px', fontSize: 12 }}
                                  disabled={actionId === p.id}
                                  onClick={() => fail(p.id)}
                                >
                                  Fail
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Inline complete form */}
                      {completeId === p.id && (
                        <tr>
                          <td colSpan={6} style={{ background: '#f0fdf4', padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              <input
                                placeholder="Transaction reference (optional)"
                                value={txRef}
                                onChange={e => setTxRef(e.target.value)}
                                style={{ flex: 1, minWidth: 200 }}
                              />
                              <button
                                className="btn btn-success"
                                style={{ fontSize: 13 }}
                                disabled={actionId === p.id}
                                onClick={() => complete(p.id)}
                              >
                                {actionId === p.id ? 'Processing...' : 'Confirm Complete'}
                              </button>
                              <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={() => setCompleteId('')}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
